import React, { useCallback, useState } from 'react';
import { Formik, useFormikContext } from 'formik';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import LoadingIndicator from '~/components/LoadingIndicator';
import TextFieldFormik from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';

import { createSlaRule, slaRecipientOptionsUrl, slaTemplatesUrl, updateSlaRule } from '../api/slaRulesApi';
import { TEXTS } from '../texts';
import type { SlaRecipientOption, SlaRuleForDialog, SlaTemplateItem, SlaTemplateParameter } from '../types';

import ContextParamsFields from './ContextParamsFields';
import type { SlaRuleFormValues } from './formValues';
import { defaultContextParams, toFormValues, toWriteRequest } from './formValues';
import PreviewPanel from './PreviewPanel';
import TemplatePickerField from './TemplatePickerField';
import ThresholdsEditor from './ThresholdsEditor';
import { slaRuleValidationSchema } from './validationSchema';

interface SlaRuleDialogProps {
  organizationId: number;
  /** The list response's `entity_cap`, which the disclaimer quotes. */
  entityCap: number;
  /** The rule to edit. Omitted, the dialog creates one. */
  rule?: SlaRuleForDialog;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

const isWholeDays = (days: number | ''): boolean => days !== '' && Number.isInteger(days) && days >= 1;

/** What the preview route needs: it classifies on the schedule alone, so the actions may be unfinished. */
const canPreviewValues = (values: SlaRuleFormValues): boolean =>
  values.display_name.trim() !== '' &&
  values.metabase_card_id !== '' &&
  values.thresholds.every((threshold) => isWholeDays(threshold.sla_days) && isWholeDays(threshold.repeat_every_days));

interface SlaRuleDialogInnerProps {
  organizationId: number;
  entityCap: number;
  rule?: SlaRuleForDialog;
  onCancel: () => void;
}

const SlaRuleDialogInner: React.FC<SlaRuleDialogInnerProps> = ({ organizationId, entityCap, rule, onCancel }) => {
  const { values, initialValues, isSubmitting, handleSubmit, setFieldValue } = useFormikContext<SlaRuleFormValues>();
  const {
    isLoading: isLoadingTemplates,
    isError: isErrorTemplates,
    data: templates,
  } = useDataFetcher<SlaTemplateItem[]>(slaTemplatesUrl(organizationId));
  const {
    isLoading: isLoadingRecipients,
    isError: isErrorRecipients,
    data: recipientOptions,
  } = useDataFetcher<SlaRecipientOption[]>(slaRecipientOptionsUrl(organizationId));

  const ruleId = rule?.id ?? null;
  // Without these the card and the actions are off screen, so a save would carry what the admin cannot see.
  const isMetadataError = isErrorTemplates || isErrorRecipients;
  // Each action's message-template field fetches its own list, and a failed one hides the template the save would send.
  const [templatesLoadFailedFields, setTemplatesLoadFailedFields] = useState<ReadonlySet<string>>(new Set());
  const handleTemplatesLoadFailedChange = useCallback((fieldId: string, isFailed: boolean) => {
    setTemplatesLoadFailedFields((previous) => {
      const next = new Set(previous);
      if (isFailed) {
        next.add(fieldId);
      } else {
        next.delete(fieldId);
      }
      return next;
    });
  }, []);
  const isOpenedCard = values.metabase_card_id === initialValues.metabase_card_id;
  // A card that breaks the contract has no derivable level, so an edited rule whose card broke since
  // keeps the level it was saved at.
  const isClaimLevelCard = (cardId: number | ''): boolean =>
    (templates?.find((template) => template.card_id === cardId)?.entity_level ?? rule?.entity_level) !== 'exposure';
  const isClaimLevel = isClaimLevelCard(values.metabase_card_id);
  // A newly picked card's params stay empty until its metadata seeds the defaults, so a save or
  // preview before then would carry none of them. A metadata read that fails keeps it that way.
  const [defaultsSeededForCard, setDefaultsSeededForCard] = useState<number | '' | null>(null);
  const isAwaitingCardDefaults =
    (ruleId === null || !isOpenedCard) &&
    values.metabase_card_id !== '' &&
    defaultsSeededForCard !== values.metabase_card_id;

  // A card's parameters are its own, and the backend refuses a slug the card does not declare, so
  // another card's values never carry over. Back on the card the form was opened with, the values
  // it stores apply again.
  useFormikChangeListener<SlaRuleFormValues>({
    listenForKeys: ['metabase_card_id'],
    onChange: (newValues, previousValues) => {
      // A card picked again before the next one loaded is remounted, and seeds its defaults anew.
      setDefaultsSeededForCard(null);
      setFieldValue(
        'context_params',
        newValues.metabase_card_id === initialValues.metabase_card_id ? initialValues.context_params : {}
      );
      // Message templates are claim- or exposure-level, and the picker lists only the card's level, so
      // one chosen for the other level would be saved while showing as unavailable.
      if (isClaimLevelCard(newValues.metabase_card_id) !== isClaimLevelCard(previousValues.metabase_card_id)) {
        setFieldValue(
          'thresholds',
          newValues.thresholds.map((threshold) => ({
            ...threshold,
            actions: threshold.actions.map((action) => ({ ...action, generic_template_id: '' })),
          }))
        );
      }
    },
    runOnFirstRender: false,
  });

  // Only a newly picked card takes its defaults: an edit that keeps its card keeps what it stores.
  const handleParametersLoaded = (parameters: SlaTemplateParameter[]) => {
    if (ruleId === null || !isOpenedCard) {
      setFieldValue('context_params', defaultContextParams(parameters));
    }
    setDefaultsSeededForCard(values.metabase_card_id);
  };

  return (
    <CardDialog
      isDialog
      maxWidth="md"
      fullWidth
      title={ruleId === null ? TEXTS.ADD_RULE_TITLE : TEXTS.EDIT_RULE_TITLE}
      onClose={onCancel}
      preventClose={isSubmitting}
      footerActions={
        <>
          <CancelButton onClick={onCancel} disabled={isSubmitting} />
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={isSubmitting || isMetadataError || templatesLoadFailedFields.size > 0 || isAwaitingCardDefaults}
          >
            {TEXTS.SAVE}
          </Button>
        </>
      }
    >
      <AlertBanner
        className="mb-20"
        alertType={AlertBanner.ALERT_TYPES.INFO}
        title={TEXTS.ALERT_TITLE}
        note={TEXTS.RULE_ISSUES_DISCLAIMER(entityCap)}
        withIcon
      />
      <TextFieldFormik
        id="display_name"
        label={TEXTS.RULE_NAME_LABEL}
        className="mb-20"
        fullWidth
        disabled={isSubmitting}
      />
      {isLoadingTemplates || isErrorTemplates || !templates ? (
        <LoadingIndicator isError={isErrorTemplates} />
      ) : (
        <TemplatePickerField templates={templates} disabled={isSubmitting} />
      )}
      <SwitchFormik id="is_business_days" label={TEXTS.BUSINESS_DAYS_LABEL} disabled={isSubmitting} />
      {values.metabase_card_id !== '' ? (
        <ContextParamsFields
          organizationId={organizationId}
          cardId={values.metabase_card_id}
          onParametersLoaded={handleParametersLoaded}
        />
      ) : null}
      {isLoadingRecipients || isErrorRecipients || !recipientOptions ? (
        <LoadingIndicator isError={isErrorRecipients} />
      ) : (
        <ThresholdsEditor
          recipientOptions={recipientOptions}
          organizationId={organizationId}
          isClaimLevel={isClaimLevel}
          onTemplatesLoadFailedChange={handleTemplatesLoadFailedChange}
        />
      )}
      <PreviewPanel
        organizationId={organizationId}
        ruleId={ruleId}
        canPreview={canPreviewValues(values) && !isAwaitingCardDefaults}
        isClaimLevel={isClaimLevel}
      />
    </CardDialog>
  );
};

const SlaRuleDialog: React.FC<SlaRuleDialogProps> = ({ organizationId, entityCap, rule, onCancel, onSaved }) => {
  return (
    <Formik<SlaRuleFormValues>
      initialValues={toFormValues(rule)}
      validationSchema={slaRuleValidationSchema}
      onSubmit={async (values) => {
        try {
          const body = toWriteRequest(values);
          if (rule) {
            await updateSlaRule(organizationId, rule.id, body);
          } else {
            await createSlaRule(organizationId, body);
          }
          await onSaved();
          onCancel();
        } catch {
          // Swallowed so the dialog stays open with the admin's input: the api layer already
          // reported the failure, and Formik clears `isSubmitting` once the returned promise settles.
        }
      }}
    >
      <SlaRuleDialogInner organizationId={organizationId} entityCap={entityCap} rule={rule} onCancel={onCancel} />
    </Formik>
  );
};

export default SlaRuleDialog;
